import { useEffect, useState } from 'react';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { asyncGetParamsWithGroupParent } from '../../../../../services/param.service';
import { ParamEnum } from '../../../../../shared/types/shared';
import { MotiveListActions } from './actions';
import { TableTooltip } from '../../../../components/general/table';
import { DateTooltip } from '../../../../components/general/dateTooltip';
import { Motive } from '../../../../../shared/types/systemConfiguration/motives';
import { Grid } from '@mui/material';
import { isOnlyViewProfile } from '../../../../../shared/types/profiles';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../redux/store';

export const useMotiveList = (filters?: Motive) => {
    const { logUser } = useSelector((state: RootState) => state.general);
    const { type } = logUser || {};
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<Motive[]>([]);
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);
    const [total, setTotal] = useState<number>(0);
    const [finalFilters, setFinalFilters] = useState<Motive | object>({});

    const paginator = (model: GridPaginationModel) => {
        if (model.page !== page) {
            setPage(model.page);
        } else if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
        }
    };

    const columns: GridColDef<Motive>[] = [];

    if (!isOnlyViewProfile(type)) {
        columns.push({
            field: 'actions',
            headerName: 'Acciones',
            width: 100,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => (
                <MotiveListActions
                    name={row.row.name}
                    active={row.row.active as boolean}
                />
            ),
        });
    }

    columns.push(
        {
            field: 'name',
            headerName: 'Nombre',
            minWidth: 150,
            flex: 0.5,
        },
        {
            field: 'parent',
            headerName: 'Tipo PQR',
            minWidth: 120,
            flex: 0.5,
            renderCell: (row) => (
                <TableTooltip value="">
                    <Grid container direction="column" spacing={1}>
                        {(row.row.parent as string[])?.map((item) => (
                            <Grid item key={item as string}>
                                {item as string}
                            </Grid>
                        ))}
                    </Grid>
                </TableTooltip>
            ),
        },
        {
            field: 'active',
            headerName: 'Activo',
            minWidth: 80,
            flex: 0.4,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => <>{row.row.active ? 'Si' : 'No'}</>,
        },
        {
            field: 'created_at',
            headerName: 'Fecha de creación',
            minWidth: 120,
            flex: 0.6,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => <DateTooltip date={row.row.created_at} />,
        }
    );

    useEffect(() => {
        setPage(0);
        setFinalFilters(filters || {});
    }, [filters]);

    useEffect(() => {
        setLoading(true);
        let name: string | undefined;
        let active: boolean | undefined;
        let parent: string[] | undefined;

        if (
            finalFilters &&
            'name' in finalFilters &&
            'active' in finalFilters &&
            'parent' in finalFilters
        ) {
            ({ name, active, parent } = finalFilters as {
                name?: string;
                active?: boolean;
                parent?: string[];
            });
        }

        asyncGetParamsWithGroupParent(
            ParamEnum.PQR_MOTIVE,
            parent,
            name,
            '',
            active,
            page,
            pageSize
        )
            .then((res: any) => {
                setRows(res.items);
                setTotal(res.length);
            })
            .finally(() => setLoading(false));
    }, [page, pageSize, finalFilters]);

    return {
        columns,
        rows,
        page,
        pageSize,
        paginator,
        loading,
        total,
    };
};
